import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ModulesService } from '../../../_services/modules/modules.service';
import { FormBase } from '../../../_classes/form-base.class';
import { SessionService } from '../../../_services/session/session.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import IUser from '../../../_interfaces/IUser';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Observable } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { EResources } from '../../../_resources/EResources';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';

interface ILoginPagePayload {
  modules_id: number;
  sites_id: number;
}

@Component({
  standalone: true,
  selector: 'app-form-user-login-page',
  templateUrl: './form-user-login-page.component.html',
  styleUrls: ['./form-user-login-page.component.scss'],
  imports: [
    ToastModule,
    CommonModule,
    SelectModule,
    TranslatePipe,

    FormsModule,
    ButtonModule,
    AdminFormActionsComponent,
  ],
})
export class FormUserLoginPageComponent
  extends FormBase<ILoginPagePayload>
  implements OnInit, OnChanges
{
  @Input('user')
  public currentUser: IUser;

  public sites: any[] = [];
  public selectedSite: any;

  public modules: any[] = [];
  public allModules: any[] = [];
  public selectedModule: any;
  public submitting = false;

  constructor(
    public session: SessionService,
    public messageService: MessageService,
    public translate: TranslateService,
    public modulesService: ModulesService,
    public customersService: CustomersService,
  ) {
    super(session, messageService, translate);
    this.mode = 'edit';
  }

  getModules() {
    const customerId = this.getActiveCustomerId();
    if (!customerId) {
      this.modules = [];
      this.selectedModule = {
        id: 0,
        name: this.translate.instant('MODULES.SELECTOR'),
      };
      return;
    }

    this.modulesService
      .getModules({ id: customerId } as any)
      .subscribe((modules) => {
        this.allModules = modules.map((module) => {
          module.name = this.translate.instant(module.name);
          return module;
        });
        this.syncModulesForSelectedSite();
      });
  }

  getSites() {
    const customerId = this.getActiveCustomerId();
    if (!customerId) {
      this.sites = [];
      this.selectedSite = {
        id: 0,
        name: this.translate.instant('SITES.SELECTOR'),
      };
      return;
    }

    this.customersService.getSites(customerId).subscribe((sites) => {
      this.sites = [...(sites || [])];
      this.sites.unshift({
        id: 0,
        name: this.translate.instant('SITES.SELECTOR'),
      });

      const currentLoginPage = this.getCurrentLoginPage();
      if (currentLoginPage) {
        this.selectedSite =
          this.sites.find((site) => +site.id === +currentLoginPage.sites_id) ||
          this.sites[0];
      } else {
        this.selectedSite =
          this.sites.find((site) => +site.id > 0) || this.sites[0];
      }

      this.syncModulesForSelectedSite();
    });
  }

  onSelectedSiteChange(): void {
    this.syncModulesForSelectedSite();
  }

  clear(): void {}

  get hasValidSelection(): boolean {
    return !!this.selectedSite?.id && !!this.selectedModule?.id;
  }

  get activeSelectionText(): string {
    if (!this.hasValidSelection) {
      return this.translate.instant('ADMIN.USERS.LOGIN_PAGE.NO_SELECTION');
    }

    return this.translate.instant('ADMIN.USERS.LOGIN_PAGE.ACTIVE_VALUE', {
      module: this.selectedModule?.name || '-',
      site: this.selectedSite?.name || '-',
    });
  }

  generatePayload(): ILoginPagePayload {
    return {
      modules_id: +this.selectedModule.id,
      sites_id: +this.selectedSite.id,
    };
  }

  getResourceName(): string {
    return EResources.USERS_LOGIN_PAGES;
  }

  updateFn(payload: ILoginPagePayload): Observable<any> {
    const customerId = this.getActiveCustomerId();
    return this.customersService.updateLoginPage(
      customerId,
      this.currentUser.id,
      payload.modules_id,
      payload.sites_id,
    );
  }

  createFn(payload: ILoginPagePayload): Observable<any> {
    throw new Error('La creación no está soportada para la página de login.');
  }

  properties(): string[] {
    return ['module', 'site'];
  }

  validate(): Promise<void> {
    return new Promise((resolve, reject) => {
      const errors: any = {};
      if (!this.selectedModule || this.selectedModule.id === 0) {
        errors['module'] = this.translate.instant(
          'ADMIN.USERS.LOGIN_PAGE.MODULE_REQUIRED',
        );
      }
      if (!this.selectedSite || this.selectedSite.id === 0) {
        errors['site'] = this.translate.instant(
          'ADMIN.USERS.LOGIN_PAGE.SITE_REQUIRED',
        );
      }
      if (Object.keys(errors).length > 0) {
        reject(errors);
      } else {
        resolve();
      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.getModules();
    this.getSites();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['currentUser'] || changes['currentUser'].firstChange) {
      return;
    }

    this.getModules();
    this.getSites();
  }

  override async execute(): Promise<void> {
    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }

  private syncModulesForSelectedSite(): void {
    const placeholder = {
      id: 0,
      name: this.translate.instant('MODULES.SELECTOR'),
    };

    const selectedSiteId = +(this.selectedSite?.id || 0);
    const filteredModules = this.filterModulesByPermission(
      this.allModules,
      selectedSiteId,
    );
    this.modules = [placeholder, ...filteredModules];

    const currentLoginPage = this.getCurrentLoginPage();
    if (currentLoginPage?.modules_id) {
      const loginModule = this.modules.find(
        (module) => +module.id === +currentLoginPage.modules_id,
      );
      this.selectedModule = loginModule || this.modules[0];
      return;
    }

    if (
      this.selectedModule &&
      this.modules.some((module) => +module.id === +this.selectedModule.id)
    ) {
      return;
    }

    this.selectedModule = this.modules[0];
  }

  private getCurrentLoginPage(): any | null {
    return (
      this.currentUser?.loginPage ||
      (this.currentUser as any)?.loginPages?.[0] ||
      null
    );
  }

  private getActiveCustomerId(): number {
    return +(
      this.customer?.id ||
      this.currentUser?.customers_id ||
      this.session.getEffectiveCustomerId() ||
      0
    );
  }

  private filterModulesByPermission(modules: any[], siteId: number): any[] {
    const requiredPermissionsByModule: Record<string, string[]> = {
      appointments: [
        'APPOINTMENTS.READ',
        'APPOINTMENTS.CREATE',
        'APPOINTMENTS.UPDATE',
        'APPOINTMENTS.DELETE',
      ],
      dashboard: ['DASHBOARD.READ', 'STATISTICS.READ'],
      incidents: ['INCIDENTS.READ'],
      patients: [
        'PATIENTS.READ',
        'PATIENTS.CREATE',
        'PATIENTS.UPDATE',
        'PATIENTS.DELETE',
      ],
      sales: ['SALES.READ', 'SALES.CREATE', 'SALES.UPDATE', 'SALES.DELETE'],
      statistics: ['STATISTICS.READ'],
      analytics: ['STATISTICS.READ'],
      tasks: [],
    };

    return (modules || []).filter((module) => {
      const identifier = this.normalizeModuleIdentifier(module?.identifier);
      const requiredPermissions = requiredPermissionsByModule[identifier] || [];

      if (!requiredPermissions.length) {
        return true;
      }

      return this.targetUserHasPermissionOnSite(siteId, requiredPermissions);
    });
  }

  private normalizeModuleIdentifier(
    identifier: string | null | undefined,
  ): string {
    const raw = `${identifier || ''}`.trim().toLowerCase();
    if (raw === 'stats') return 'statistics';
    return raw;
  }

  private targetUserHasPermissionOnSite(
    siteId: number,
    permissions: string[],
  ): boolean {
    if (this.currentUser?.isAdmin || this.currentUser?.superuser) {
      return true;
    }

    const userRoleOnSite = (this.currentUser?.hasRoles || []).find(
      (userHasRole: any) => +userHasRole?.sites_id === +siteId,
    );

    const roleResources = userRoleOnSite?.role?.resources || [];
    return roleResources.some((resourcePermission: any) => {
      if (!resourcePermission?.value) {
        return false;
      }

      const resourceName =
        resourcePermission?.resource?.name || resourcePermission?.name || '';
      return permissions.some(
        (permission) =>
          `${resourceName}`.toUpperCase() === `${permission}`.toUpperCase(),
      );
    });
  }
}
